import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Clock, Users, DollarSign, Percent, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/layout/Navigation";
import Footer from "@/components/layout/Footer";

// Recent sales images - cafe interiors
const recentSalesImages = [
    "https://images.pexels.com/photos/1995010/pexels-photo-1995010.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/2074130/pexels-photo-2074130.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1307698/pexels-photo-1307698.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/2253643/pexels-photo-2253643.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/683039/pexels-photo-683039.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1855214/pexels-photo-1855214.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/2788792/pexels-photo-2788792.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1002740/pexels-photo-1002740.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1579739/pexels-photo-1579739.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/2788488/pexels-photo-2788488.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/3201783/pexels-photo-3201783.jpeg?auto=compress&cs=tinysrgb&w=400",
    "https://images.pexels.com/photos/1833306/pexels-photo-1833306.jpeg?auto=compress&cs=tinysrgb&w=400",
];

const HomePage = () => {
    return (
        <div className="min-h-screen bg-white">
            <Navigation />
            
            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-50 to-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        {/* Text Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center lg:text-left"
                        >
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                                How much is your Cafe{" "}
                                <span className="text-primary">Worth?</span>
                                <br />
                                <span className="text-secondary">Find out Now!</span>
                            </h1>
                            
                            <p className="text-lg text-gray-600 mb-8 max-w-lg mx-auto lg:mx-0">
                                Get a free, instant valuation of your café based on real sales data 
                                and industry benchmarks. No obligation, results in under 3 minutes.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <Link to="/cafe-valuer">
                                    <Button
                                        size="lg"
                                        className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/30 transition-all duration-300 transform hover:-translate-y-1 rounded-xl px-8 py-6 text-lg font-semibold group w-full sm:w-auto"
                                        data-testid="hero-valuation-btn"
                                    >
                                        Value my Cafe
                                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                                    </Button>
                                </Link>
                                <a href="tel:1800370240">
                                    <Button
                                        size="lg"
                                        variant="outline"
                                        className="border-2 border-gray-300 hover:border-primary hover:text-primary rounded-xl px-8 py-6 text-lg font-semibold w-full sm:w-auto"
                                    >
                                        <Phone className="w-5 h-5 mr-2" />
                                        1800 370240
                                    </Button>
                                </a>
                            </div>
                        </motion.div>

                        {/* Hero Image */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                <img
                                    src="https://images.pexels.com/photos/1995010/pexels-photo-1995010.jpeg?auto=compress&cs=tinysrgb&w=800"
                                    alt="Modern café interior"
                                    className="w-full h-[350px] lg:h-[450px] object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                            </div>
                            
                            {/* Decorative Elements */}
                            <div className="absolute -top-4 -right-4 w-24 h-24 bg-secondary/20 rounded-full blur-2xl" />
                            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/20 rounded-full blur-2xl" />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Why Cafe Sales Experts Section */}
            <section id="why-us" className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                            Why Cafe Sales Experts?
                        </h2>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        {/* Image */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <img
                                src="https://images.pexels.com/photos/2788792/pexels-photo-2788792.jpeg?auto=compress&cs=tinysrgb&w=800"
                                alt="Café environment"
                                className="w-full h-[300px] lg:h-[400px] object-cover rounded-2xl shadow-lg"
                            />
                        </motion.div>

                        {/* Content */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="space-y-6"
                        >
                            <p className="text-gray-600 leading-relaxed">
                                At Cafe Sales Experts, we are dedicated to helping café owners sell their 
                                businesses efficiently and effectively. With our expertise in the café industry, 
                                we ensure a seamless transition from listing to sale.
                            </p>
                            
                            <p className="text-gray-600 leading-relaxed">
                                We advertise across <strong>9 Business for Sale Websites</strong>, including Seek, CRE, 
                                BSale, AnyBusiness and more. We have a massive database of café buyers to help 
                                you go from for sale to sold faster.
                            </p>

                            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                                <p className="text-gray-800 font-medium">
                                    Plus we charge <span className="text-primary font-bold text-xl">4% commission</span> on 
                                    sale compared to market rates of 8% to 10%.
                                </p>
                            </div>

                            <p className="text-gray-800 font-semibold text-lg">
                                Effective, Friendly, Affordable.
                            </p>
                            <p className="text-gray-600">
                                Our team have sold hundreds of cafes and understand café sales inside out.
                                Cafe Sales Experts Business Brokers are the right choice for your café sale.
                            </p>

                            {/* Key Benefits */}
                            <div className="grid sm:grid-cols-3 gap-4 pt-4">
                                <div className="text-center p-4 bg-gray-50 rounded-xl">
                                    <Users className="w-8 h-8 text-primary mx-auto mb-2" />
                                    <p className="font-bold text-2xl text-gray-900">900+</p>
                                    <p className="text-sm text-gray-500">Businesses Sold</p>
                                </div>
                                <div className="text-center p-4 bg-gray-50 rounded-xl">
                                    <DollarSign className="w-8 h-8 text-primary mx-auto mb-2" />
                                    <p className="font-bold text-2xl text-gray-900">$10M+</p>
                                    <p className="text-sm text-gray-500">Commissions Saved</p>
                                </div>
                                <div className="text-center p-4 bg-gray-50 rounded-xl">
                                    <Percent className="w-8 h-8 text-primary mx-auto mb-2" />
                                    <p className="font-bold text-2xl text-gray-900">4%</p>
                                    <p className="text-sm text-gray-500">Fair Commission</p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Want to Learn More / Contact Section */}
            <section className="py-16 lg:py-20 bg-gradient-to-br from-primary/5 to-secondary/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12">
                        {/* Left - CTA */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="text-center lg:text-left"
                        >
                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                                Want to learn more?
                            </h2>
                            <h3 className="text-2xl font-semibold text-primary mb-6">
                                Give us a call! 1800 370240
                            </h3>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                Have a question about how we can match your specific needs? Send us a message, 
                                or give us a call.
                            </p>
                            <p className="text-gray-600 leading-relaxed mb-8">
                                We&apos;re always happy to offer a <strong>free appraisal</strong> on your café and explain 
                                how the process works from advert to your next adventure!
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <a href="tel:1800370240">
                                    <Button
                                        size="lg"
                                        className="bg-primary hover:bg-primary/90 text-white rounded-xl px-8 py-6 text-lg font-semibold w-full sm:w-auto"
                                    >
                                        <Phone className="w-5 h-5 mr-2" />
                                        Call Now
                                    </Button>
                                </a>
                                <Link to="/cafe-valuer">
                                    <Button
                                        size="lg"
                                        variant="outline"
                                        className="border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-xl px-8 py-6 text-lg font-semibold w-full sm:w-auto"
                                    >
                                        Get Free Valuation
                                    </Button>
                                </Link>
                            </div>
                        </motion.div>

                        {/* Right - Contact Info */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="bg-white rounded-2xl shadow-lg p-8"
                        >
                            <h3 className="text-xl font-bold text-gray-900 mb-6">Cafe Sales Experts</h3>
                            <p className="text-gray-600 mb-6">Australia Wide Cafe Sales</p>
                            
                            <div className="space-y-4 mb-8">
                                <a 
                                    href="tel:1800370240" 
                                    className="flex items-center gap-3 text-primary font-semibold text-lg hover:underline"
                                >
                                    <Phone className="w-5 h-5" />
                                    1800 370240
                                </a>
                            </div>

                            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary" />
                                Business Hours
                            </h4>
                            <div className="space-y-2 text-gray-600">
                                <div className="flex justify-between">
                                    <span>Monday - Friday</span>
                                    <span>8:30am - 4:30pm</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Saturday</span>
                                    <span>Closed</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Sunday</span>
                                    <span>Closed</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Recent Cafe Sales Section */}
            <section id="recent-sales" className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                            Recent Cafe Sales
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            A selection of cafés we&apos;ve successfully sold across Australia
                        </p>
                    </motion.div>

                    {/* Image Grid */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                    >
                        {recentSalesImages.map((img, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                className="relative group overflow-hidden rounded-xl aspect-square"
                            >
                                <img
                                    src={img}
                                    alt={`Recent cafe sale ${index + 1}`}
                                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-colors duration-300" />
                                <div className="absolute bottom-2 right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    SOLD
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="text-center mt-12"
                    >
                        <Link to="/cafe-valuer">
                            <Button
                                size="lg"
                                className="bg-secondary hover:bg-secondary/90 text-white rounded-xl px-8 py-6 text-lg font-semibold"
                            >
                                Get Your Free Valuation
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default HomePage;
